import type { Id, TrackingRefreshOutcome } from "@estelle/domain";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { useDataSource } from "@/adapters/data-source";
import { refreshMessage, type OutcomeMessage } from "@/modules/orders/tracking";

/** What the server holds one order to between refreshes. The wait after a success is this long. */
const SUCCESS_WAIT_SECONDS = 60;

export interface RefreshTracking {
  /** Asking the server now. */
  pending: boolean;
  /** Seconds before another refresh would be accepted; 0 when one would. */
  waitSeconds: number;
  /** What the last ask came to, and which shipment's button it was. */
  result: { shipmentId: Id; message: OutcomeMessage } | null;
  /** Ask. A second call while one is in flight, or inside the wait, does nothing. */
  refresh: (shipmentId: Id) => void;
}

/**
 * "Refresh tracking" on an order (#527, Part B): ask the courier now, then say
 * what came of it.
 *
 * One of these per order, shared by that order's shipment cards, because the
 * server refreshes the order and rate-limits it as a whole: a button on each
 * card that kept its own count would let the second card be pressed straight
 * after the first and be refused.
 *
 * It cannot be fired twice. The request is held by a ref the moment the first
 * click arrives, not by the render that follows it, so two clicks in the same
 * frame still send one request; and after an answer the button stays down for as
 * long as the server will refuse, counting it out loud.
 *
 * Every outcome, including a refusal, is an ordinary answer with words. The
 * order and the lists are re-read after any that asked a courier, so a stage
 * that moved is on screen without a reload.
 */
export function useRefreshTracking(orderId: Id): RefreshTracking {
  const data = useDataSource();
  const queryClient = useQueryClient();

  const inFlight = useRef(false);
  const [until, setUntil] = useState(0);
  const [clock, setClock] = useState(() => Date.now());
  const [result, setResult] = useState<RefreshTracking["result"]>(null);

  const mutation = useMutation({
    mutationFn: ({ shipmentId }: { shipmentId: Id }) =>
      data.tracking
        .refreshOrder(orderId)
        .then((outcome) => ({ shipmentId, outcome })),
    onSuccess: ({ shipmentId, outcome }) => {
      setResult({ shipmentId, message: refreshMessage(outcome) });
      const wait = waitAfter(outcome);
      if (wait > 0) {
        setUntil(Date.now() + wait * 1000);
        setClock(Date.now());
      }
      if (outcome.kind === "refreshed") {
        void queryClient.invalidateQueries({
          queryKey: ["orders", "detail", orderId],
        });
        void queryClient.invalidateQueries({ queryKey: ["orders", "list"] });
        void queryClient.invalidateQueries({
          queryKey: ["orders", "shipments"],
        });
      }
    },
    onError: (_error, { shipmentId }) => {
      setResult({
        shipmentId,
        message: {
          tone: "problem",
          text: "Could not refresh tracking. Check your connection and try again.",
        },
      });
    },
    onSettled: () => {
      inFlight.current = false;
    },
  });

  // Count the wait out while there is one, once a second, and stop after.
  const waiting = until > clock;
  useEffect(() => {
    if (!waiting) return;
    const timer = setInterval(() => {
      setClock(Date.now());
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [waiting]);

  return {
    pending: mutation.isPending,
    waitSeconds: waiting ? Math.ceil((until - clock) / 1000) : 0,
    result,
    refresh: (shipmentId) => {
      if (inFlight.current || waiting) return;
      inFlight.current = true;
      mutation.mutate({ shipmentId });
    },
  };
}

/** How long the server will refuse another ask after this answer. */
function waitAfter(outcome: TrackingRefreshOutcome): number {
  switch (outcome.kind) {
    case "refreshed":
      return SUCCESS_WAIT_SECONDS;
    case "just_refreshed":
      return outcome.retryAfterSeconds;
    default:
      return 0;
  }
}
